import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, Outlet } from 'react-router-dom'
import { useAtom, useSetAtom } from 'jotai'
import {
  cellsAtom,
  cellsInitAtom,
  clearRundownAtom,
  columnsAtom,
  columnsInitAtom,
  cuesAtom,
  cuesInitAtom,
  rundownAtom,
} from '../../store/rundown.store'
import { runnerAtom } from '../../store/runner.store.js'
import { RundownToken } from '../../axios'
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '../../firebase.js'
import { parseSnapshot } from '@rundown-studio/utils'
import { Layout } from '../../components/layout'
import RundownError from './rundown-error.jsx'
import { ConnectionChecker } from '../../features/connection-checker'
import { getRundownEvent, rundownPlan } from '../../firestore.js'
import { planAtom } from '../../store/plan.store.js'
import {
  clearEventAtom,
  eventAtom,
  eventInitAtom,
} from '../../store/event.store.js'
import {
  RUNDOWN_COLLECTION,
  CUE_COLLECTION,
  COLUMN_COLLECTION,
  RUNNER_COLLECTION,
} from '@rundown-studio/consts'
import {
  parseCellSnapshot,
  parseColumnSnapshot,
  parseCueSnapshot,
  parseRundownSnapshot,
} from '../../utils/parseSnapshotWrappers.js'
import { useAddRundownPrivateNotesColumn } from '../../entities/rundown/api/hooks.js'
import { useUser } from '../../entities/user/api/hooks.js'

import { useMentionsListener } from '../../entities/mentions/api/firestore/listeners.js'
import { useRundownListener } from '../../entities/rundown/index.js'
import { useCellListener } from '@/features/rundown/rundown-page.hooks.ts'
import { useAuthSync } from '../../entities/user/api/firebase/subscriptions.js'
import { tickControlAtom } from '../../shared/store/tick.store.js'
import { clockAtom } from '../../shared/store/clock.store.ts'
import { timestampsAtom } from '../../store/timestamps.store.js'

let unsubscribers = []

export default function RundownParent() {
  const { rundownId } = useParams()
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const [error, setError] = useState(null)
  const [rundown, setRundown] = useAtom(rundownAtom)
  const [event, setEvent] = useAtom(eventAtom)
  const [eventInit, setEventInit] = useAtom(eventInitAtom)
  const [plan, setPlan] = useAtom(planAtom)
  const [runner, setRunner] = useAtom(runnerAtom)
  const [cuesInit, setCuesInit] = useAtom(cuesInitAtom)
  const [cues, setCues] = useAtom(cuesAtom)
  const [cellsInit, setCellsInit] = useAtom(cellsInitAtom)
  const setCells = useSetAtom(cellsAtom)
  const [columnsInit, setColumnsInit] = useAtom(columnsInitAtom)
  const [columns, setColumns] = useAtom(columnsAtom)
  const clearRundown = useSetAtom(clearRundownAtom)
  const clearEvent = useSetAtom(clearEventAtom)
  const startClock = useSetAtom(clockAtom)

  const hasPrivateNotesColumn = useRef(false)

  const { data: user } = useUser()
  const {
    mutate: addPrivateNotesColumn,
    isPending: isAddingPrivateNotesColumn,
  } = useAddRundownPrivateNotesColumn({})

  useAuthSync()

  useEffect(() => {
    const cleanup = startClock()

    return () => cleanup()
  }, [startClock])

  //
  // Create a 100ms interval if rundown is running
  // Use this interval to create the moment and timestamps
  // If not running, update every minute
  //
  const setTickControl = useSetAtom(tickControlAtom)
  const isRunning = useMemo(
    () => runner?.timesnap?.running,
    [runner?.timesnap?.running],
  )

  // Start/stop ticks based on running state
  useEffect(() => {
    setTickControl(isRunning)

    return () => setTickControl(false)
  }, [isRunning, setTickControl])

  const createTimestamps = useSetAtom(timestampsAtom)
  // Update moment data only when runner/cues change (NOT on every tick!)
  useEffect(() => {
    createTimestamps()
    // setAtom is a stable reference (like useState's set), so we don't need it as dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadDone, rundown, runner, cuesInit, cues])

  //
  // Initiate the rundown token
  //
  const requestToken = useCallback(async () => {
    const url = new URL(window.location)
    const signature = url.searchParams.get('signature')
    url.searchParams.delete('signature')
    RundownToken.setParameters(rundownId, url.href, signature)
  }, [rundownId])

  //
  // Get the payment plan associated with the rundown
  //
  const requestPlan = useCallback(async () => {
    const { data: rundownPlanData } = await rundownPlan(rundownId, {
      toast: false,
    })
    setPlan(rundownPlanData)
  }, [rundownId, setPlan])

  //
  // Get the payment plan associated with the rundown
  //
  const requestEvent = useCallback(
    async (eventId) => {
      if (!eventId) return setEventInit(true)
      const { data: rundownEventData } = await getRundownEvent(rundownId, {
        toast: false,
      })
      setEvent(rundownEventData)
      setEventInit(true)
    },
    [rundownId, setEvent, setEventInit],
  )

  const handleRundownUpdate = useCallback(
    (snapshot) => {
      if (!snapshot.exists())
        return setError({
          status: 404,
          message: 'This rundown does not exist.',
        })
      const rundownData = parseRundownSnapshot(snapshot)
      setRundown(rundownData)
      requestEvent(rundownData.eventId)
    },
    [requestEvent, setRundown],
  )

  useRundownListener({
    rundownId,
    callback: handleRundownUpdate,
  })

  // //
  // // Get the teams colours
  // //
  // const requestTeamColours = useCallback(async (teamId) => {
  //   const writeAccess = RundownToken.access === ACCESS_WRITE
  //   if(!teamId || !writeAccess) return
  //   const {data: teamData} = await getTeam(teamId, true)
  //   setTeamColours(teamData.customColours)
  // }, [rundown?.teamId])

  //
  // Subscribe to Firestore updates on all relevant documents
  // Only resolve when intital payloads are loaded
  //
  useCellListener((querySnapshot) => {
    const cells = []
    querySnapshot.forEach((doc) => {
      cells.push(parseCellSnapshot(doc))
    })
    setCells(cells)
    setCellsInit(true)
  })
  useMentionsListener({
    rundownId,
  })

  const requestData = useCallback(async () => {
    const promises = []

    // Subscribe to cues
    promises.push(
      new Promise((resolve) => {
        const cuesQuery = query(
          collection(db, RUNDOWN_COLLECTION, rundownId, CUE_COLLECTION),
          where('deletedAt', '==', null),
        )
        unsubscribers.push(
          onSnapshot(cuesQuery, (querySnapshot) => {
            const cues = []
            querySnapshot.forEach((doc) => {
              cues.push(parseCueSnapshot(doc))
            })
            setCues(cues)
            setCuesInit(true)
            resolve()
          }),
        )
      }),
    )

    // Subscribe to columns
    promises.push(
      new Promise((resolve) => {
        const columnsQuery = query(
          collection(db, RUNDOWN_COLLECTION, rundownId, COLUMN_COLLECTION),
          where('deletedAt', '==', null),
        )
        unsubscribers.push(
          onSnapshot(columnsQuery, (querySnapshot) => {
            const columns = []
            querySnapshot.forEach((doc) => {
              columns.push(parseColumnSnapshot(doc))
            })

            setColumns(columns)
            setColumnsInit(true)
            resolve()
          }),
        )
      }),
    )

    await Promise.all(promises)
  }, [rundownId, setColumns, setColumnsInit, setCues, setCuesInit])

  const unsubscribeAll = useCallback(() => {
    unsubscribers.forEach((unsub) => unsub())
    unsubscribers = []
    // Clear out rundown and event store
    clearEvent()
    setEventInit(false)
    return clearRundown()
  }, [clearEvent, clearRundown, setEventInit])

  //
  // Perform the initial load
  //
  const initialLoad = useCallback(async () => {
    try {
      await requestToken()
      await requestPlan()
      await requestData()
    } catch (err) {
      console.error(err)
      setError(err)
    }
  }, [requestToken, requestData, requestPlan])

  useEffect(() => {
    initialLoad()
    return unsubscribeAll
  }, [initialLoad, unsubscribeAll])

  /**
   * Avoid fatal error by resetting initial load when the store unloads during component hot-reloading in development
   */
  if (import.meta.env.DEV) {
    if (initialLoadDone && !rundown) setInitialLoadDone(false)
  }

  //
  // Subscribe to the runner, resubscribe if runnerId changes
  //
  useEffect(() => {
    if (!rundown?.runnerId) return setRunner(null)
    return onSnapshot(doc(db, RUNNER_COLLECTION, rundown?.runnerId), (doc) => {
      setRunner(parseSnapshot(doc))
    })
  }, [rundown?.runnerId, setRunner])

  //
  // Set initialLoadDone=true once all data is loaded
  //
  useEffect(() => {
    if (initialLoadDone) return
    if (!rundown) return
    if (!plan) return
    if (!cuesInit) return
    if (!cellsInit) return
    if (!columnsInit) return
    if (!eventInit) return
    setInitialLoadDone(true)
  }, [
    cellsInit,
    columnsInit,
    cuesInit,
    eventInit,
    initialLoadDone,
    plan,
    rundown,
  ])

  // If rundown doesn't have a private notes column - create it
  useEffect(() => {
    if (hasPrivateNotesColumn.current) return
    if (!user) return
    if (!columns?.length) return

    const isPrivateNotesColumnExist = columns.find(
      ({ privateUid }) => privateUid === user.uid,
    )

    if (isPrivateNotesColumnExist) {
      hasPrivateNotesColumn.current = true
    } else {
      addPrivateNotesColumn({
        rundownId,
        privateUid: user.uid,
      })
      hasPrivateNotesColumn.current = true
    }
  }, [addPrivateNotesColumn, columns, rundownId, user])

  if (initialLoadDone && !isAddingPrivateNotesColumn)
    return (
      <>
        <Outlet
          context={{
            rundown,
            cues,
            columns,
            runner,
            plan,
            event,
          }}
        />
        <ConnectionChecker />
      </>
    )
  else if (error) return <RundownError error={error} />
  else return <RundownLoader />
}

function RundownLoader() {
  return <Layout title="Loading..." loading />
}
